/* eslint-disable no-console */
import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';

@Component
export class CartItemGroupedBike extends ThemeComponent {
    @Prop bikeId!: string;
    @Prop bikePermalink!: string;

    @Ref({ required: false }) removeItemButton!: HTMLButtonElement[];
    @Ref({ required: false }) toggleProducts!: HTMLButtonElement[];
    @Ref({ required: false }) toggleProductsText!: HTMLElement[];
    @Ref({ required: false }) groupedProducts!: HTMLElement[];

    private errorMsg: string = '';
    private isExpanded: boolean = false;
    private domRoot: HTMLElement | null = null;

    init() {
        this.domRoot = this.getDomRoot(true);
        this.attachEventListeners();

        // Re-attach listeners after cart updates (when DOM is replaced)
        document.addEventListener('cartChanged', () => {
            // Use setTimeout to ensure DOM has been updated
            setTimeout(() => {
                this.domRoot = this.getDomRoot(true);
                this.attachEventListeners();
            }, 100);
        });
    }

    private getDomRoot(force: boolean = false): HTMLElement | null {
        const inlineRoot = this.querySelector('[data-grouped-bike-root]') as HTMLElement | null;
        if (inlineRoot) {
            return inlineRoot;
        }

        const bikeId = this.getAttribute('bike-id');
        const neighbours: (Element | null)[] = [this.nextElementSibling, this.previousElementSibling];

        for (const neighbour of neighbours) {
            if (neighbour instanceof HTMLElement && neighbour.dataset.groupedBikeRow === 'true') {
                if (!bikeId || neighbour.dataset.groupedBikeId === bikeId) {
                    return neighbour;
                }
            }
        }

        if (bikeId) {
            const table = this.closest('table');
            if (table) {
                const selector = `tr[data-grouped-bike-row="true"][data-grouped-bike-id="${bikeId}"]`;
                const fallbackRow = table.querySelector(selector) as HTMLElement | null;
                if (fallbackRow) {
                    return fallbackRow;
                }
            }

            const globalSelector = `tr[data-grouped-bike-row="true"][data-grouped-bike-id="${bikeId}"]`;
            const globalFallback = document.querySelector(globalSelector) as HTMLElement | null;
            if (globalFallback) {
                return globalFallback;
            }
        }

        return null;
    }

    private attachEventListeners(attempt: number = 0) {
        const resolvedRoot = this.getDomRoot();
        if (!resolvedRoot) {
            if (attempt < 5) {
                setTimeout(() => this.attachEventListeners(attempt + 1), 100);
            }
            return;
        }

        const root = resolvedRoot as HTMLElement;

        const removeButtons = Array.from(
            root.querySelectorAll('[data-ref="removeItemButton"]') as NodeListOf<HTMLButtonElement>
        );
        const toggleButtons = Array.from(
            root.querySelectorAll('[data-ref="toggleProducts"]') as NodeListOf<HTMLButtonElement>
        );
        const toggleTexts = Array.from(
            root.querySelectorAll('[data-ref="toggleProductsText"]') as NodeListOf<HTMLElement>
        );

        // Update refs so other methods can rely on them if needed
        this.removeItemButton = removeButtons as unknown as HTMLButtonElement[];
        this.toggleProducts = toggleButtons as unknown as HTMLButtonElement[];
        this.toggleProductsText = toggleTexts as unknown as HTMLElement[];

        for (const button of removeButtons) {
            this.$listen(button, 'click', () => this.handleRemove());
        }

        for (const button of toggleButtons) {
            this.$listen(button, 'click', () => this.handleToggleProducts());
        }

        if ((toggleButtons.length === 0 || toggleTexts.length === 0) && attempt < 5) {
            setTimeout(() => this.attachEventListeners(attempt + 1), 50);
        }
    }

    handleToggleProducts() {
        // Try to find products container via refs first, then querySelector
        const root = this.getDomRoot();
        if (!root) {
            return;
        }

        const productsContainer = this.groupedProducts && this.groupedProducts.length > 0
            ? this.groupedProducts[0]
            : root.querySelector('[data-ref="groupedProducts"]') as HTMLElement;

        if (!productsContainer) {
            return;
        }

        const productLimit = parseInt(productsContainer.dataset.productLimit || '5', 10);
        const allProducts = productsContainer.querySelectorAll('.cart-item__grouped-product');

        // Try to find toggle text via refs first, then querySelector
        let toggleText: HTMLElement | null = null;
        if (this.toggleProductsText && this.toggleProductsText.length > 0) {
            toggleText = this.toggleProductsText[0];
        } else {
            toggleText = root.querySelector('[data-ref="toggleProductsText"]') as HTMLElement;
        }

        if (this.isExpanded) {
            // Collapse - hide products beyond limit
            for (const product of allProducts) {
                const productIndex = parseInt((product as HTMLElement).dataset.productIndex || '0', 10);
                if (productIndex > productLimit) {
                    (product as HTMLElement).classList.add('cart-item__grouped-product--hidden');
                }
            }
            if (toggleText) {
                toggleText.textContent = 'Show More';
            }
            this.isExpanded = false;
        } else {
            // Expand - show all products
            for (const product of allProducts) {
                (product as HTMLElement).classList.remove('cart-item__grouped-product--hidden');
            }
            if (toggleText) {
                toggleText.textContent = 'Show Less';
            }
            this.isExpanded = true;
        }
    }

    async handleRemove() {
        const itemIdsString = this.dataset.itemIds || '';

        const lineItemIds = itemIdsString
            .split(',')
            .map((id) => Number.parseInt(id.trim(), 10))
            .filter((id): id is number => !Number.isNaN(id));

        const targetBikeId = this.getAttribute('bike-id') || this.bikeId;
        const targetIds = new Set<number>(lineItemIds);
        const shouldMatchBike = Boolean(targetBikeId);
        const shouldMatchIds = !shouldMatchBike && targetIds.size > 0;
        type CartEntry = (typeof cart.items)[number];

        const matchesGroup = (cartItem: CartEntry) => {
            if (shouldMatchBike) {
                return cartItem.properties?.['_bike_id'] === targetBikeId;
            }

            if (shouldMatchIds) {
                const lineItemId = cartItem.id as number | undefined;
                return typeof lineItemId === 'number' && targetIds.has(lineItemId);
            }

            return false;
        };

        const updates = cart.items.map((cartItem) => (
            matchesGroup(cartItem) ? 0 : cartItem.quantity
        ));

        const matchedCount = cart.items.reduce((count, cartItem) => (
            matchesGroup(cartItem) ? count + 1 : count
        ), 0);

        if (matchedCount === 0) {
            this.$log.error(`No matching items found for bike group ${this.bikeId}`);
            return;
        }

        this.toggleGlobalSpinner(true);
        try {
            await cart.updateItemQuantities(updates);
            this.errorMsg = '';
            this.hideErrorMessage();
        } catch (event: unknown) {
            const { message } = event as Error;
            this.$log.error('Cart item group removal error', message);
            this.errorMsg = `${message ? message.split('.')[0] : ''}`;
            this.showErrorMessage();
        } finally {
            this.toggleGlobalSpinner(false);
        }

        document.dispatchEvent(new Event('cartChanged'));
    }

    private toggleGlobalSpinner(isActive: boolean) {
        const spinner = document.querySelector('[data-ref="updatingCart"]') as HTMLElement | null;
        if (!spinner) {
            return;
        }
        spinner.classList.toggle('loading-spinner--active', isActive);
    }

    showErrorMessage() {
        const cartWrapper = document.querySelector('.cart-error');
        if (cartWrapper && this.errorMsg) {
            cartWrapper.textContent = this.errorMsg;
            cartWrapper.classList.add('show');
        }
    }

    hideErrorMessage() {
        const cartWrapper = document.querySelector('.cart-error');
        if (cartWrapper) {
            cartWrapper.textContent = '';
            cartWrapper.classList.remove('show');
        }
    }
}

